import React from 'react';
import _ from 'lodash';
import { Button, Box, Stack, Typography, Checkbox, Select, Chip, FormControl, OutlinedInput, MenuItem } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components-core/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components-core/others/AlertDialogContainer';
import { submitEventAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components-core/ConfirmDialog';

const months = [
  { label: 'styczeń', value: 1 },
  { label: 'luty', value: 2 },
  { label: 'marzec', value: 3 },
  { label: 'kwiecień', value: 4 },
  { label: 'maj', value: 5 },
  { label: 'czerwiec', value: 6 },
  { label: 'lipiec', value: 7 },
  { label: 'sierpień', value: 8 },
  { label: 'wrzesień', value: 9 },
  { label: 'październik', value: 10 },
  { label: 'listopda', value: 11 },
  { label: 'grudzień', value: 12 }
];

export const EventForm = props => {
  const {
    data,
    submitCallback,
    handleClose,
    allSeasons,
    allResorts,
    allAgeRanges,
    allCamps
  } = props;

  const { loading, mutate } = useMutation(submitEventAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    gross_value: Yup.number().required('To pole nie może być puste'),
    resort: Yup.string().required('To pole nie może być puste'),
    schedule: Yup.string().required('To pole nie może być puste')
  });
  const initialValues = {
    name: data ? data.name || '' : '',
    gross_value: data ? data.gross_value || '' : '',
    resort: data ? data.resort || '' : '',
    schedule: data ? data.schedule || '' : '',
    instrucors_emails: data ? data.instrucors_emails || '' : '',
    months: data ? data.months || [] : [],
    for_children: data ? data.for_children : false,
    levels: data ? data.levels : false,
    family_discount: data ? data.family_discount : false,
    partial_payment: data ? data.partial_payment : false,
    monthly_payments: data ? data.monthly_payments : true,
    generate_deductions: data ? data.generate_deductions : true,
    yearly_payment: data ? data.yearly_payment : false,
    send_schedule_update: data ? data.send_schedule_update : false
  };
  const handleSubmit = async values => {
    const { error, payload } = await mutate(
      values,
      data ? 'PUT' : 'POST',
      data ? data.id : undefined
    );
    if (!error) {
      submitCallback(
        payload,
        data ? 'edit' : 'created',
        'success',
        data ? 'Zaktualizowano imprezę' : 'Dodano nową imprezę',
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się ${data ? 'edytować' : 'dodać nowej'} imprezy`);
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(
        data,
        'delete',
        'success',
        `Usunięto impreze ${data.name}`,
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć imprezy ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: data ? true : false,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" flex="1">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Nazwa
            </Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa zajęć"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Cena brutto (zł)
            </Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="gross_value"
              id="gross_value"
              type="text"
              placeholder="cena zajęć"
              value={formik.values.gross_value}
              onChange={formik.handleChange}
              error={
                formik.touched.gross_value && Boolean(formik.errors.gross_value)
              }
              helperText={
                formik.touched.gross_value && formik.errors.gross_value
              }
              debounceTimeout={300}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Miesiące
            </Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="months"
              name="months"
              multiple
              options={months}
              value={formik.values.months}
              onChange={formik.handleChange}
              error={formik.touched.months && Boolean(formik.errors.months)}
              helperText={formik.touched.months && formik.errors.months}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Ośrodek
            </Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="resort"
              name="resort"
              options={allResorts}
              value={formik.values.resort}
              onChange={formik.handleChange}
              error={formik.touched.resort && Boolean(formik.errors.resort)}
              helperText={formik.touched.resort && formik.errors.resort}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Harmonogram
            </Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              id="schedule"
              name="schedule"
              multiline
              rows={10}
              value={formik.values.schedule}
              onChange={formik.handleChange}
              error={formik.touched.schedule && Boolean(formik.errors.schedule)}
              helperText={formik.touched.schedule && formik.errors.schedule}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
            Wyślij informacje o zmianie harmonogramu po edycji
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="send_schedule_update"
                name="send_schedule_update"
                checked={formik.values.send_schedule_update}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Prowadzący zajęcia e-mail&apos;e
            </Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              id="instrucors_emails"
              name="instrucors_emails"
              multiline
              rows={5}
              value={formik.values.instrucors_emails}
              onChange={formik.handleChange}
              error={
                formik.touched.instrucors_emails &&
                Boolean(formik.errors.instrucors_emails)
              }
              helperText={
                formik.touched.instrucors_emails &&
                formik.errors.instrucors_emails
              }
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Dla dzieci
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="for_children"
                name="for_children"
                checked={formik.values.for_children}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Stopnie
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="levels"
                name="levels"
                checked={formik.values.levels}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Rabat rodzinny
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="family_discount"
                name="family_discount"
                checked={formik.values.family_discount}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Opłata roczna
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="yearly_payment"
                name="yearly_payment"
                checked={formik.values.yearly_payment}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Opłata częściowa
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="partial_payment"
                name="partial_payment"
                checked={formik.values.partial_payment}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Generuj opłaty miesięczne (wyłącz przy indywidualnym harmonogramie rozliczeń; przy włączonym generuje TYLKO dla wybranych wyzej miesięcy)
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="monthly_payments"
                name="monthly_payments"
                checked={formik.values.monthly_payments}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            mb={2}
          >
            <Typography variant="subtitle2" flex="1 1 10%">
              Generuj potrącenia
            </Typography>
            <Box
              sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }}
              display="flex"
              justifyContent="flex-start"
            >
              <Checkbox
                id="generate_deductions"
                name="generate_deductions"
                checked={formik.values.generate_deductions}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>

        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
        >
          <AddIcon />
          Zapisz
        </Button>
        {data ? (
          <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
            Usuń
          </Button>
        ) : null}
      </form>
      <ConfirmDialog
        title="Usunąć Zajęcia?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć tą imprezę?
      </ConfirmDialog>
    </>
  );
};
